<template>
  <b-container fluid role="tablist" class="options-picker steps">
    <step
      v-if="applyToLayers.length > 1"
      :step="{ name: specialCases.stepNames.applyToLayers }"
      class="active"
    >
      <div class="p-3">
        <b-btn
          v-for="(layer, i) in [null, ...applyToLayers]"
          :key="i"
          variant="outline-primary"
          class="mr-2"
          :class="{
            active: layer === applyToLayer
          }"
          @click="() => handleDoorApplyTo(layer)"
        >
          {{layer ? layer.name : 'All Doors' }}
        </b-btn>
      </div>
    </step>
    <step
      v-for="(step, stepIndex) in currentLayer.steps.filter(s => s.hasItems !== false)"
      :key="step.name"
      :step="step"
      :id="`step-${stepIndex}`"
      :class="{
        collapsable: true,
        disabled: !isStepAvailable(step, stepIndex),
        active: currentLayer.currentStepIndex === stepIndex,
      }"
    >
      <b-card-header
        @click.prevent="isStepAvailable(step, stepIndex) && handleStepClick(step, stepIndex)"
        :aria-controls="`step-collapser-${slug(step.name)}`"
        :aria-expanded="currentLayer.currentStepIndex === stepIndex ? 'true' : 'false'"
        role="tab"
        class="d-flex"
        slot="header"
      >
        <div class="flex-auto">{{ step.name }}</div>
        <div v-if="isStepAvailable(step, stepIndex)" class="flex-none">
          <i class="fa fa-pencil"></i>
        </div>
      </b-card-header>
      <b-collapse
        :id="`step-collapser-${slug(step.name)}`"
        accordion="steps"
        role="tabpanel"
        :visible="currentLayer.currentStepIndex === stepIndex"
        @shown="scrollTo()"
      >
        <div v-if="step.name === specialCases.stepNames.product" class="p-2">
          <b-btn
            block
            :variant="isCommercial ? 'primary' : undefined"
            @click="setCommercial(!isCommercial)"
          >
            View {{isCommercial ? 'Residential' : 'Commercial'}} Products
          </b-btn>
        </div>
        <div v-if="step.type === 'door-dimensioner'" class="p-3">
          <door-dimensioner
            :layer="currentLayer"
            show-presets
            dimensions-label="Or, enter your exact garage door size:"
            create-label="Confirm Size"
            @save="handleDimensionsPicked"
          />
        </div>
        <div v-else-if="step.type === 'door-picker'">
          <door-picker
            @picked="handleDoorPicked"
            @create="handleDoorCreate"
            @remove="handleDoorRemove"
          />
        </div>
        <b-tabs
          v-else-if="step.sections"
          v-for="section in step.sections.filter(s => s.hasItems !== false
            && (slug(s.name) !== specialCases.autoAdvance.section || !noWindows)
            && !(isCommercial
              && s.name === specialCases.sectionNames.windowInserts
              && (getStepItems(step, specialCases.sectionNames.windowInserts) || []).length === 1
            ))"
          :key="section.name"
          :id="`section-${slug(section.name)}`"
        >
          <b-tab
            v-for="category in section.categories.filter(c => c.hasItems !== false)"
            :key="category.id"
          >
            <template slot="title">
              {{category.name}}
              <a
                v-if="isCommercial
                  && step.name === specialCases.stepNames.windows
                  && category.name === specialCases.categoryNames.position"
                v-b-popover="specialCases.info.windowPosition"
                class="info-button p-2"
              >
                <i class="fa fa-info-circle" />
              </a>
              <a
                v-if="category.name === specialCases.categoryNames.upgrades"
                v-b-popover="specialCases.info.upgradesPopover"
                class="info-button p-2"
              >
                <i class="fa fa-info-circle" />
              </a>
              <b-btn
                v-if="category.name === specialCases.categoryNames.upgrades"
                variant="primary"
                @click.prevent="handleItemClick(
                  { name: specialCases.multiSelect.done, type: 'button' },
                  category,
                  section,
                  step
                )"
                class="overflow-ellipsis ml-2"
              >
                {{ specialCases.multiSelect.done }}
              </b-btn>
            </template>
            <div
              class="d-flex justify-content-left p-2"
              v-if="category.name === specialCases.categoryNames.upgrades"
            >
              {{specialCases.info.upgradesSelectionText.content}}
            </div>
            <template
              v-for="itemGroup in category.itemGroups || [category]"
            >
              <header
                :key="`items-collapser-header-${itemGroup.name}`"
                v-if="category.itemGroups"
                :aria-controls="`items-collapser-${itemGroup.name}`"
                :aria-expanded="itemGroup.collapsed !== true ? 'true' : 'false'"
                role="tab"
                class="items-collapser-header"
                :class="{ active: itemGroup.collapsed !== true }"
                @click="itemGroup.collapsed = !itemGroup.collapsed"
              >
                <span>{{ itemGroup.name }}</span>
                <i class="fa fa-chevron-down" />
              </header>
              <b-collapse
                :key="`items-collapser-${itemGroup.name}`"
                :id="`items-collapser-${itemGroup.name}`"
                role="tabpanel"
                :visible="itemGroup.collapsed !== true"
                :class="category.itemGroups && 'items-collapser'"
              >
                <b-card
                  v-for="item in itemGroup.items"
                  :key="item.guid || item.name"
                  :img-src="item.thumbnail"
                  no-body
                  class="linked-card"
                  :class="{ active: item.selected }"
                >
                  <div
                    v-if="step.name === specialCases.stepNames.thermalConstruction"
                    class="card-body"
                  >
                    <template>
                      <h6>Thermal Rating:</h6>
                      <div class="rating">
                        <img
                          v-for="i in parseInt(item.thermometers)"
                          :key="i"
                          src="/static/images/thermal_icon.png"
                          alt="•"
                          height="20"
                        />
                      </div>
                      <output>R-{{ item.rValue }}</output>
                      <h7>Model: {{ item.modelNumber }}</h7>
                    </template>
                    <ul>
                      <li>{{ item.gauge }}</li>
                      <li>{{ item.thickness }}</li>
                      <li>{{ item.constructionType }}</li>
                      <li v-if="item && item.windLoadInformation">
                        {{ item.windLoadInformation }}</li>
                      <li>{{ item.doorType }}</li>
                      <li>{{ item.insulation }}</li>
                    </ul>
                  </div>
                  <div v-else class="card-body">
                    {{ item.name }}
                  </div>
                  <a
                    :title="item.name"
                    class="card-link"
                    @click.prevent="handleItemClick(item, category, section, step)"
                  ></a>
                </b-card>
              </b-collapse>
            </template>
          </b-tab>
        </b-tabs>
      </b-collapse>
    </step>

    <footer class="d-flex justify-content-center p-2">
      <b-btn @click.prevent="handleReload()" variant="default" size="lg">
        <i class="fa fa-undo" /> &nbsp;
        Start Over
      </b-btn>
    </footer>
  </b-container>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import get from 'lodash.get';
import slug from 'slug';
import Step from '@/components/Step';
import Configurator from '@/components/Configurator';
import DoorDimensioner from '@/components/DoorDimensioner';
import {
  dimensionsToString,
  packDimensions,
  unpackDimensions,
  setProductPixelDimensions,
  pointsChange,
  getDoorPointsFromShape,
} from '@/helpers/dimensions';
import DoorPicker from '@/components/DoorPicker';
import Spinner from '@/components/utility/Spinner';

export default {
  name: 'options-picker',
  props: {
    products: Array,
    loadingPattern: Boolean,
    loadingData: Boolean,
  },
  data() {
    return {
      currentScrollTo: undefined,
    };
  },
  computed: {
    ...mapState([
      'specialCases',
      'applyToLayer',
      'currentLayer',
      'currentSavedDoor',
      'noWindows',
    ]),
    ...mapGetters([
      'isCommercial',
      'applyToLayers',
      'currentProduct',
      'currentProject',
    ]),
  },
  watch: {
    products: {
      handler() {
        // update steps on product change (e.g. load)
        this.refreshSteps();
      },
      deep: true,
    },
    isCommercial: {
      handler() {
        // update steps on product change (e.g. load)
        this.refreshSteps();
      },
      deep: true,
    },
  },
  methods: {
    get,
    ...mapMutations([
      'setCommercial',
      'setCurrentProduct',
      'setApplyToLayer',
      'setCurrentLayer',
      'setCurrentSavedDoor',
      'setNoWindows',
    ]),
    slug(text) {
      return slug(text).toLowerCase();
    },
    scrollTo(element = undefined) {
      // cancel existing
      if (this.currentScrollTo) {
        this.currentScrollTo();
      }
      // start new scroll
      this.currentScrollTo = this.$scrollTo(element || `#step-${this.currentLayer.currentStepIndex}`, 500, {
        container: '#products > .flex-fill',
      });
    },
    getStepItems(step, sectionName, categoryName = sectionName) {
      const section = step && step.sections.find(s => s.name === sectionName);
      const category = section && section.categories.find(c => c.name === categoryName);
      return category && category.items;
    },
    refreshSteps(
      // reset = false,
      layer = this.currentLayer) {
      const product = this.currentProduct;
      const {
        stepNames,
        sectionNames,
        productNames,
        // multiSelect,
        categoryNames,
        autoAdvance,
      } = this.specialCases;

      // option selection is made in product.getSteps() -> grid_object.js:loadGrid()
      // based upon single selectedGrid, so capture multiselection steps and retain
      /* const upgradeStep = layer.steps && layer.steps
        .find(st => st.name === multiSelect.step); */

      // count the number of selected options by drilling down into structure
      // but do not count the reset option, as that should clear all other options
      /* const selectedUpgradeOptions = upgradeStep && upgradeStep.sections
        .reduce((a, v) => a.concat(v.categories || []), [])
        .reduce((a, v) => a.concat(v.items || []), [])
        .filter(i => i.selected && i.name !== multiSelect.reset)
        .reduce(a => a + 1, 0); */

      // most of the grid drilling and pruning is done here
      let productSteps = product ? product.getSteps() : [];

      // the upgrade step is a multiselect step, so prep it here and add it
      /* const upgradeStepIndex = productSteps.findIndex(st => st.name === multiSelect.step);
      if (!reset && selectedUpgradeOptions > 0 && upgradeStep && upgradeStepIndex > -1) {
        productSteps[upgradeStepIndex] = upgradeStep;
      } */

      // carve out the glass step if there are no windows selected
      if (this.noWindows) {
        const glassStepIndex = productSteps.findIndex(st => st.name === stepNames.glass);
        if (glassStepIndex > -1) {
          productSteps.splice(glassStepIndex, 1);
        }
      }

      // when single design option, the Design tab shouldn't show
      const designStep = productSteps.find(step => step.name === stepNames.design);
      if (designStep && designStep.sections[0].categories[0].items.length === 1) {
        // select the single design so it filters up through
        const designGridTab = designStep.sections[0].categories[0].items[0];
        product.selectGridOption(designGridTab.tab, designGridTab.grid);
        // skip the step
        productSteps = productSteps.filter(step => step.name !== stepNames.design);
      }

      const productsWithNoWindowsRemoved = [
        productNames.aluminumFullView,
      ];

      if (product && productsWithNoWindowsRemoved.includes(product.name)) {
        // don't show users the internal-render-only 'No Windows' option
        const windowsStep = productSteps.find(st => st.name === stepNames.windows);
        if (windowsStep) {
          windowsStep.sections.forEach((section) => {
            section.categories.forEach((category) => {
              const noWindowsIndex = category.items
                .findIndex(item => item.name.toLowerCase() === autoAdvance.noWindows.toLowerCase());
              if (noWindowsIndex > -1) {
                // remove it from the user's options
                category.items.splice(noWindowsIndex, 1);

                // preselect the next item instead
                const [firstRemainingItem] = category.items;
                if (firstRemainingItem && !category.selected) {
                  product.selectGridOption(firstRemainingItem.tab, firstRemainingItem.grid);
                }
              }
            });
          });
        }
      }

      if (product && this.isCommercial) {
        const windowsStep = productSteps.find(step => step.name === stepNames.windows);
        const items = this.getStepItems(windowsStep, sectionNames.windowInserts);
        const hasOnlyOneOption = items && items.length === 1;
        if (hasOnlyOneOption) {
          const [item] = items;
          product.selectGridOption(item.tab, item.grid);
        }
      }
      const chiData = window.Renoworks.CHIData;

      // Filter out color options
      const colorStep = productSteps.find(step => step.name === stepNames.color);
      if (colorStep) {
        const colorSection = colorStep.sections
          .find(section => section.name === 'color');
        if (colorSection) {
          // Filter out unsupported categories
          const allowedColorCategories = [
            'Solid Colors',
            'Solid Color',
            'Accents Woodtones',
            'CHI Powder Coat',
            'Woodgrain Powder Coat',
            'RAL Powder Coat',
            'Morton Powder Coat',
            'Custom Colors',
            'Woodtones',
            'Wood',
          ];
          colorSection.categories = colorSection.categories.filter(
            category => allowedColorCategories.includes(category.name));

          // Get list of options for each tab
          const scsCategory = colorSection.categories
            .find(category => category.name === 'Solid Colors');
          const scCategory = colorSection.categories
            .find(category => category.name === 'Solid Color');
          const awCategory = colorSection.categories
            .find(category => category.name === 'Accents Woodtones');
          const chipcCategory = colorSection.categories
            .find(category => category.name === 'CHI Powder Coat');
          const ralpcCategory = colorSection.categories
            .find(category => category.name === 'RAL Powder Coat');
          const mpcCategory = colorSection.categories
            .find(category => category.name === 'Morton Powder Coat');
          const wgpcCategory = colorSection.categories
            .find(category => category.name === 'Woodgrain Powder Coat');
          const cfcCategory = colorSection.categories
            .find(category => category.name === 'Custom Colors');
          const wtCategory = colorSection.categories
            .find(category => category.name === 'Woodtones');
          const wCategory = colorSection.categories
            .find(category => category.name === 'Wood');

          // Get list of allowed color options
          const pm = chiData.getProductModel();
          if (pm) {
            const colorOptions = pm.colorOptions;
            const standardFrontColorOptions = colorOptions.standardFrontColorOptions.map(
              item => item.displayName.toLowerCase());
            const accentFrontColorOptions = colorOptions.accentFrontColorOptions.map(
              item => item.displayName.toLowerCase());
            const chiPowderCoatOptions = colorOptions.chiPowderCoatOptions.map(
              (item) => {
                if (item.displayName) {
                  return item.displayName.toLowerCase();
                }
                return item.name.toLowerCase();
              });
            const ralColorOptions = colorOptions.ralColorOptions.map(
              item => item.displayName.toLowerCase());
            const woodgrainPowderCoatOptions = colorOptions.woodgrainPowderCoatOptions.map(
              item => item.displayName.toLowerCase());
            const mortonColorOptions = colorOptions.mortonColorOptions.map(
              item => item.displayName.toLowerCase());
            const customFrontColorOptions = colorOptions.customFrontColorOptions.map(
              item => item.displayName.toLowerCase());

            // Filter tab options
            // Solid Colors
            if (scCategory) {
              scCategory.items = scCategory.items
                .filter(item => standardFrontColorOptions.includes(item.name.toLowerCase()));
            }
            if (scsCategory) {
              scsCategory.items = scsCategory.items
                .filter(item => standardFrontColorOptions.includes(item.name.toLowerCase()));
            }
            // Accents Woodtones
            if (awCategory) {
              awCategory.items = awCategory.items
                .filter(item => accentFrontColorOptions.includes(item.name.toLowerCase()));
            }
            // CHI Powder Coat
            if (chipcCategory) {
              chipcCategory.items = chipcCategory.items
                .filter(item => chiPowderCoatOptions.includes(item.name.toLowerCase()));
            }
            // Woodgrain Powder Coat
            if (wgpcCategory) {
              wgpcCategory.items = wgpcCategory.items
                .filter(item => woodgrainPowderCoatOptions.includes(item.name.toLowerCase()));
            }
            // RAL Powder Coat
            if (ralpcCategory) {
              ralpcCategory.items = ralpcCategory.items
                .filter(item => ralColorOptions.includes(item.name.toLowerCase()));
            }
            // Morton Powder Coat
            if (mpcCategory) {
              mpcCategory.items = mpcCategory.items
                .filter(item => mortonColorOptions.includes(item.name.toLowerCase()));
            }
            // Custom Colors
            if (cfcCategory) {
              cfcCategory.items = cfcCategory.items
                .filter(item => customFrontColorOptions.includes(item.name.toLowerCase()));
            }
            // Woodtones
            if (wtCategory) {
              wtCategory.items = wtCategory.items
                .filter(item => customFrontColorOptions.includes(item.name.toLowerCase()));
            }
            // Wood
            if (wCategory) {
              wCategory.items = wCategory.items
                .filter(item => standardFrontColorOptions.includes(item.name.toLowerCase()));
            }

            // Filter out empty tabs
            colorSection.categories = colorSection.categories.filter(
              category => category.items.length);
          }

          colorStep.sections.forEach((section) => {
            const moreColorsCategory = section.categories
              .find(category => category.name === categoryNames.ralColors);
            if (moreColorsCategory) {
              const optionNames = moreColorsCategory.items
                .map(({ grid }) => grid && grid.optionName)
                .filter(Boolean)
                .filter((v, i, a) => a.indexOf(v) === i);
              moreColorsCategory.itemGroups = optionNames.map((optionName) => {
                const items = moreColorsCategory.items
                  .filter(item => item.grid && item.grid.optionName === optionName);
                return {
                  ...moreColorsCategory,
                  name: optionName,
                  items,
                  collapsed: !items.some(({ selected }) => selected),
                };
              });
            }
          });
        }
      }

      // Filter out window options
      const windowsStep = productSteps.find(step => step.name === stepNames.windows);
      if (windowsStep) {
        // Get list of options for tabs
        const windowsSection = windowsStep.sections
          .find(section =>
            section.name.toLowerCase() === 'windows' ||
            section.name.toLowerCase() === 'window' ||
            section.name.toLowerCase() === 'glass');
        if (windowsSection) {
          const glassCategory = windowsSection.categories
            .find(category => category.name === 'Glass');
          const styleliteCategory = windowsSection.categories
            .find(category => category.name === 'StyleLite');
          const designerCategory = windowsSection.categories
            .find(category => category.name === 'Designer Glass');
          let insertsSection = windowsStep.sections
            .find(section => section.name === 'Window Inserts');
          if (!insertsSection) {
            insertsSection = windowsStep.sections
              .find(section => section.name.toLowerCase() === 'inserts');
          }

          // Get list of allowed glass names
          const windowGlassOptions = chiData.getWindowGlassOptions();
          // Get list of allowed inserts
          const windowInsertOptions = chiData.getWindowInserts();

          // Filter tab options
          if (glassCategory && windowGlassOptions.glass) {
            glassCategory.items = glassCategory.items
              .filter(item => windowGlassOptions.glass.includes(item.name.toLowerCase()));
          }
          if (styleliteCategory && windowGlassOptions.glass) {
            styleliteCategory.items = styleliteCategory.items
              .filter(item => windowGlassOptions.glass.includes(item.name.toLowerCase()));
            if (!styleliteCategory.items.length) {
              styleliteCategory.hasItems = false;
            }
          }
          if (designerCategory && windowGlassOptions.designer) {
            designerCategory.items = designerCategory.items
              .filter(item => windowGlassOptions.designer.includes(item.name.toLowerCase()));
            if (!designerCategory.items.length) {
              designerCategory.hasItems = false;
            }
          }
          if (insertsSection && windowInsertOptions.glass) {
            insertsSection.categories.forEach((category) => {
              category.items = category.items
                .filter(item => windowInsertOptions.glass.includes(item.name.toLowerCase()));
              if (!category.items.length) {
                category.hasItems = false;
              }
            });

            // Select default if nothing is selected
            let selected = null;
            insertsSection.categories.every((category) => {
              selected = category.items.find(item => item.selected);
              return !selected;
            });

            if (!selected && insertsSection.categories.length) {
              const insertsTab = product.tabs.find(tab => tab.name === 'Window Inserts');
              const insertsCategory = insertsSection.categories[0];
              if (insertsTab && insertsCategory.items.length > 0) {
                product.selectGridOption(insertsTab, insertsCategory.items[0].grid);
              }
            }
          }
        }
      }

      // build the final set of steps
      const dimensionsString = layer && dimensionsToString(unpackDimensions(layer));
      const steps = [
        {
          name: stepNames.size,
          type: 'door-dimensioner',
          sections: [{
            name: stepNames.size,
            categories: [{
              name: stepNames.size,
              items: dimensionsString ? [{
                name: dimensionsString,
                selected: true,
              }] : [],
              selected: !!dimensionsString,
            }],
            selected: !!dimensionsString,
          }],
          selected: !!dimensionsString,
        },
        // show door picker step only if logged in
        window.Renoworks.User.loggedIn && {
          name: stepNames.savedDoors,
          type: 'door-picker',
        },
        {
          name: 'Products',
          // commercial products have no categories atm
          sections: chiData.getCollections(this.isCommercial),
          selected: true,
        },
        ...(chiData.useStyle() ? [{
          name: 'Style',
          sections: chiData.getStyles(),
          selected: true,
        }] : []),
        {
          name: 'Design',
          sections: chiData.getDesigns(),
          selected: true,
        },
        {
          name: 'Thermal Requirements / Construction',
          sections: chiData.getModels(),
          selected: true,
        },
        ...productSteps,
      ].filter(Boolean);
      if (this.currentProject) {
        steps.shift();
      }
      layer.steps = steps;
    },
    isStepAvailable(step, stepIndex) {
      return step.type || stepIndex <= this.currentLayer.farthestStepIndex;
    },
    handleStepChanged(stepIndex, bumpFarthest = false) {
      this.currentLayer.currentStepIndex = stepIndex;

      if (bumpFarthest) {
        this.currentLayer.farthestStepIndex = stepIndex;
      }
    },
    handleStepClick(step, stepIndex) {
      if (step.name === this.specialCases.stepNames.savedDoors) {
        if (this.currentSavedDoor) {
          window.Renoworks.User
            .updateProduct(this.currentSavedDoor.id, this.currentProduct, this.currentLayer.name);
        }
      }
      this.handleStepChanged(stepIndex);
    },
    async handleDoorApplyTo(layer) {
      if (layer) {
        if (!this.currentLayer.product) {
          // override/clone product into each layer (thereby allowing distinct configurations)
          this.applyToLayers.forEach((l) => {
            l.product = this.currentProduct && this.currentProduct.clone();
            l.steps = this.currentLayer.steps;
            l.currentStepIndex = this.currentLayer.currentStepIndex;
            l.farthestStepIndex = this.currentLayer.farthestStepIndex;
          });
        }
        this.setCurrentLayer(layer);
      } else {
        // prompt user that this will overwrite all door configs with the current door
        try {
          await this.$root.confirm(`Are you sure you want to reset all door configurations to match the currently selected one (${this.currentLayer.name})?`);
        } catch (err) {
          return; // gobble up 'error' but prevent continuing
        }

        // clear override/split products (and therefore fallback to currentProduct)
        this.applyToLayers.forEach((l) => {
          l.product = null;
          l.steps = this.currentLayer.steps;
          l.currentStepIndex = this.currentLayer.currentStepIndex;
          l.farthestStepIndex = this.currentLayer.farthestStepIndex;
        });
        this.setCurrentLayer(this.currentLayer);
      }
      this.setApplyToLayer(layer);
      this.handlePicked(true); // trigger a rerender
    },
    async handlePicked(noAdvance = false, avoidEmitting = false) {
      // refresh steps
      this.refreshSteps();

      // advance to next step, if necessary
      if (!noAdvance) {
        if (this.currentLayer.currentStepIndex !== 0) {
          this.handleStepChanged(this.currentLayer.currentStepIndex + 1, true);
        } else {
          // When setting size, don't reset, and go back to the furthest selected tab
          if (this.currentLayer.farthestStepIndex === 0) {
            this.currentLayer.farthestStepIndex += 1;
          }
          this.handleStepChanged(this.currentLayer.farthestStepIndex);
        }
      }

      // save modified door to user profile
      if (window.Renoworks.User.loggedIn && this.currentProduct) {
        if (this.currentSavedDoor) {
          // update existing product, if found
          await window.Renoworks.User
            .updateProduct(this.currentSavedDoor.id, this.currentProduct, this.currentLayer.name);
        } else {
          // otherwise, save new product
          const doorID = await window.Renoworks.User
            .saveProduct(this.currentProduct, this.currentLayer.name);
          this.setCurrentSavedDoor({
            id: doorID,
          });
        }
      }

      // trigger a re-render
      if (!avoidEmitting) {
        this.$emit('picked', null);
      }
    },
    async handleDimensionsPicked(layer, dimensions) {
      // check size before continuing
      const chiData = window.Renoworks.CHIData;
      const pm = chiData.getProductModel();
      if (pm) {
        try {
          await pm.load(layer);

          const { physicalWidth, physicalHeight } = dimensions;
          const width = {
            ft: Math.floor(physicalWidth),
            in: Math.round((physicalWidth - Math.floor(physicalWidth)) * 12),
          };
          const height = {
            ft: Math.floor(physicalHeight),
            in: Math.round((physicalHeight - Math.floor(physicalHeight)) * 12),
          };

          // Get allowed width/heights from model
          const { allowedWidths, allowedHeights } = pm;
          // We don't have the information at this point
          const widthIn = (width.ft * 12) + width.in;
          const heightIn = (height.ft * 12) + height.in;
          if (!allowedWidths.includes(widthIn)) {
            let cancel = false;
            this.$root.confirm(`The door width ${width.ft}ft ${width.in}in is not supported.
              You will reset your selections if you proceed. Do you wish to continue?`).then((ok) => {
              if (ok) {
                chiData.reset();
                this.handleDimensionsPickedStep2(layer, dimensions);
              } else {
                cancel = true;
              }
            });
            if (cancel) {
              return;
            }
          } else if (!allowedHeights.includes(heightIn)) {
            let cancel = false;
            this.$root.confirm(`The door height ${height.ft}ft ${height.in}in is not supported.
              You will reset your selections if you proceed. Do you wish to continue?`).then((ok) => {
              if (ok) {
                chiData.reset();
                this.handleDimensionsPickedStep2(layer, dimensions);
              } else {
                cancel = true;
              }
            });
            if (cancel) {
              return;
            }
          } else {
            this.handleDimensionsPickedStep2(layer, dimensions);
          }
        } catch (err) {
          console.warn(err); // eslint-disable-line no-console
        }
      } else {
        this.handleDimensionsPickedStep2(layer, dimensions);
      }
    },
    handleDimensionsPickedStep2(layer, dimensions) {
      layer.physicalWidth = dimensions.physicalWidth;
      layer.physicalHeight = dimensions.physicalHeight;
      // add or remove the necessary points to the layer perspective
      if (layer.doorShape !== dimensions.doorShape) {
        const pointsDelta = pointsChange(layer.doorShape, dimensions.doorShape);
        if (pointsDelta !== 0) {
          layer.perspective.changePerspectivePoints(getDoorPointsFromShape(dimensions.doorShape));
        }
        layer.doorShape = dimensions.doorShape;
      }

      // update product dimensions for saving to door (always currentLayer dimensions)
      if (this.currentProduct) {
        setProductPixelDimensions(this.currentProduct, this.currentLayer);
      }

      this.handlePicked();
    },
    handleDoorPicked(door = undefined, noAdvance = false, retries = 30) {
      // #retries corresponds to number of seconds to wait for CHI API to hydrate the product data
      this.setCurrentSavedDoor(door);

      const data = door ? door.getData() : {};
      let product = null;
      let missedGuids = '';
      this.products.forEach((p) => {
        // search for a currently loaded GridObject that matches the saved product
        if (p.name === data.name && p.rwd === data.rwd) {
          missedGuids = p.selectByGridSettings(data.settings);
          product = p;
        } else {
          // reset all non-selected products while hydrating selected one
          p.selectByGridSettings('');
        }
      });
      if (data.size) {
        const [width, height] = data.size.split('x');
        const packedDimensions = packDimensions(unpackDimensions({
          physicalWidth: width,
          physicalHeight: height,
        }));
        this.currentLayer.physicalWidth = packedDimensions.physicalWidth;
        this.currentLayer.physicalHeight = packedDimensions.physicalHeight;
      }
      this.setCurrentProduct(product);

      // fetch patterns for this product so injected extra data is hydrated
      if (missedGuids && retries > 0) {
        this.$emit('picked', null);
        // reselect after refresh of pattern data (recurse)
        setTimeout(() => {
          this.handleDoorPicked(door, noAdvance, retries - 1);
        }, 1000);
        return;
      } else if (retries === 0) {
        // TODO: warning popup?
        console.error('Thermal Requirements and Optional Upgrades were not available, so will be removed from the saved door'); // eslint-disable-line no-console
      }

      // bump up farthest step index if restored product necessitates it
      function getFarthestStepIndex(layer) {
        return layer.steps.reduce((acc, step, stepIndex) => {
          if (step.type || step.selected) {
            acc = stepIndex;
          }
          return acc;
        }, 0);
      }
      const farthestStepIndex = getFarthestStepIndex(this.currentLayer);
      this.currentLayer.farthestStepIndex = farthestStepIndex;
      this.currentLayer.currentStepIndex = farthestStepIndex;

      // progress steps
      this.handlePicked(noAdvance);
    },
    async handleDoorCreate() {
      // apply the first product to the new door
      const product = this.products[0];
      product.selected = true;
      // apply the thumbnail as a rendered image, to be updated later
      // NOTE: saveProduct will fail unless the image is valid
      if (!product.renderedImage) {
        product.renderedImage = product.thumbnail;
      }
      this.setCurrentProduct(product);

      // save default product
      const doorID = await window.Renoworks.User
        .saveProduct(product, this.currentLayer.name);
      const savedDoors = window.Renoworks.User.getProducts();
      const door = savedDoors.find(s => s.id === doorID);
      this.handleDoorPicked(door, true); // noAdvance = true, do not advance a step
    },
    handleDoorRemove(door) {
      // can't delete selected door
      if (!this.currentSavedDoor || door.id !== this.currentSavedDoor.id) {
        window.Renoworks.User.deleteProduct(door.id);
      }
    },
    async handleItemClick(theItem, theCategory, theSection, theStep) {
      const chiData = window.Renoworks.CHIData;
      // block based on size
      if (theItem instanceof window.Renoworks.CHIProductModel) {
        chiData.setProductModel(theItem);
        try {
          // Todo - show spinner
          this.$emit('changeLoadingDoor', true);
          await theItem.load(this.currentLayer);
          this.$emit('changeLoadingDoor', false);

          // Get allowed width/heights from model
          const { allowedWidths, allowedHeights } = theItem;
          // We don't have the information at this point
          const dimensions = unpackDimensions(this.currentLayer);
          const widthIn = (dimensions.width.ft * 12) + dimensions.width.in;
          const heightIn = (dimensions.height.ft * 12) + dimensions.height.in;
          if (!allowedWidths.includes(widthIn)) {
            this.$root.alert(
              `The door width ${dimensions.width.ft}ft ${dimensions.width.in}in
              is not supported for your selection.`, 'Unsupported Size',
            );
            return;
          } else if (!allowedHeights.includes(heightIn)) {
            this.$root.alert(
              `The door height ${dimensions.height.ft}ft ${dimensions.height.in}in
              is not supported for your selection.`, 'Unsupported Size',
            );
            return;
          }
        } catch (err) {
          console.warn(err); // eslint-disable-line no-console
          return;
        }
      }

      // skip and just advance to next step if selection remains unchanged
      const isAlreadySelected = (
        theStep.selected
        && theSection.selected
        && theCategory.selected
        && theItem.selected
      );

      // ensure that only one item is selected within each section of categories
      // by unselecting everything relevant first...
      if (theSection.categories) {
        theSection.categories.forEach((category) => {
          if (category.items) {
            // there can be only one selected item within a category
            category.items.forEach((item) => {
              if (item instanceof window.Renoworks.CHIProductFamily ||
                item instanceof window.Renoworks.CHIProductDesign ||
                item instanceof window.Renoworks.CHIProductModel) {
                // manually unselect product
                item.selected = false;
              } else if (this.currentProduct && item.tab) {
                this.currentProduct.unselectGridOption(item.tab);
              } /* else if (theStep.name === this.specialCases.multiSelect.step &&
                theItem.name !== this.specialCases.multiSelect.reset) {
                // multiselect do not unselect unless it is a reset action
              } */
            });
          }
        });
      }

      // ...and forcibly-reselecting only what's necessary
      /* if (theStep.name === this.specialCases.multiSelect.step &&
        theItem.name === this.specialCases.multiSelect.reset) {
        this.refreshSteps(true); // reset = true does not retain selected
      } else if (theStep.name === this.specialCases.multiSelect.step &&
        theItem.name !== this.specialCases.multiSelect.reset) {
        this.refreshSteps(); // reset = false retains selected
      } */

      theItem.selected = true;
      theCategory.selected = true;
      theSection.selected = true;
      theStep.selected = true;

      // determine if there are other sections that need selecting before proceeding
      let lastStep = true;
      let count = 1;
      let nextStepIndex = 0;
      const sections = (theStep.sections || []);
      if (theStep.name !== 'Products') {
        lastStep = false;
        sections.forEach((section) => {
          if (section.name === theSection.name) {
            if (count === sections.length) {
              lastStep = true;
            } else {
              nextStepIndex = count; // Starts at 1, so count is next index
            }
          }
          count += 1;
        });
      }
      const hasSectionsRemaining = !lastStep;

      // Special case for windows
      if (theStep.name === this.specialCases.stepNames.windows) {
        this.setNoWindows(theItem.name.toLowerCase() === this.specialCases.autoAdvance.noWindows);
      }

      // commit the changes
      if (theItem instanceof window.Renoworks.CHIProductFamily) {
        chiData.setProductFamily(theItem, theSection.productLine, theSection.name);
      } else if (theItem instanceof window.Renoworks.CHIProductDesign) {
        if (theItem.hasModelDesigns) {
          chiData.setProductStyle(theItem);
        } else {
          chiData.setProductDesign(theItem);
        }
      } else if (theItem instanceof window.Renoworks.CHIProductModelDesign) {
        chiData.setProductModelDesign(theItem);
      } else if (theItem instanceof window.Renoworks.CHIProductModel) {
        const productFamily = theSection.productFamily;
        const product = window.Renoworks.ProductController.findProductByAttribute('name', productFamily);
        const designTab = product.tabs.find(tab => tab.name === 'Design');
        if (designTab) {
          const designName = chiData.getProductDesignName();
          product.selectGridBySelection(designTab, { name: designName });
        }
        const styleTab = product.tabs.find(tab => tab.name === 'Style');
        if (styleTab) {
          const styleName = chiData.getProductStyleName();
          product.selectGridBySelection(styleTab, { name: styleName });
        }
        this.setCurrentProduct(product);
      } else if (this.currentProduct && theItem.type !== 'button') {
        this.currentProduct.selectGridOption(theItem.tab, theItem.grid);
      }

      // refresh and advance steps if necessary
      // if multiselect, then do not advance steps until the done button is selected
      /* if (theStep.name !== this.specialCases.multiSelect.step ||
        theItem.name === this.specialCases.multiSelect.done) {
        this.handlePicked(hasSectionsRemaining && !this.noWindows, isAlreadySelected);
      } */
      this.handlePicked(hasSectionsRemaining && !this.noWindows, isAlreadySelected);

      // scroll to next remaining section, if there is one
      /* if (hasSectionsRemaining &&
          (theStep.name !== this.specialCases.multiSelect.step ||
          theItem.name === this.specialCases.multiSelect.done)) {
        this.scrollTo(`#section-${this.slug(remainingSectionsInStep[0].name)}`);
      } */
      if (hasSectionsRemaining) {
        const nextStepName = sections[nextStepIndex].name;
        this.scrollTo(`#section-${this.slug(nextStepName)}`);
      }
    },
    handleReload() {
      this.$root.confirm('You will lose all unsaved changes (if any).').then(() => {
        window.location.reload();
      });
    },
  },
  created() {
    // init steps
    this.refreshSteps();

    // refresh steps on auth change
    window.jQuery(window.Renoworks.Event).on(window.Renoworks.Event.USER_LOADED, () => {
      this.refreshSteps();
    });
  },
  components: {
    Step,
    Configurator,
    DoorDimensioner,
    DoorPicker,
    Spinner,
  },
};
</script>

<style lang="scss">
.options-picker {
  .tab-pane {
    // to contain spinner
    position: relative;
    min-height: 100px;
  }

  .items-collapser-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    .fa {
      transform: rotate(0);
      transition: transform 300ms;
    }

    &.active {
      .fa {
        transform: rotate(180deg);
      }
    }
  }
  .items-collapser {
    .card {
      .card-img {
        max-width: 100%;
      }
      .card-body {
        display: none;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/OptionsPicker.vue